import { useEffect, useRef, useState } from "react";
import useReveal from "../hooks/useReveal";

export function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "in-view" : ""} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function Eyebrow({ children, light = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] ${
        light ? "text-[#e0c05a]" : "text-[#c9a227]"
      }`}
    >
      <span className="w-6 h-px bg-[#c9a227]" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, center = true, light = false }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
      <h2
        className={`font-display font-bold mt-4 text-3xl md:text-[42px] leading-[1.12] ${
          light ? "text-white" : "text-[#012a1e]"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-[17px] leading-relaxed ${light ? "text-white/70" : "text-slate-500"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function CountUp({ end, suffix = "", duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !started) {
          setStarted(true);
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.floor(eased * end));
            if (p < 1) requestAnimationFrame(tick);
            else setVal(end);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, started]);

  const display = end >= 1000 ? val.toLocaleString() : val;
  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

export function PageHero({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative bg-[#004030] overflow-hidden">
      <div className="absolute inset-0 opacity-25">
        {image && <img src={image} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#004030] via-[#004030]/95 to-[#004030]/70" />
      <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
        {eyebrow && <Eyebrow light>{eyebrow}</Eyebrow>}
        <h1 className="font-display font-bold text-white text-4xl md:text-[56px] leading-[1.08] mt-4 max-w-3xl">
          {title}
        </h1>
        {subtitle && <p className="mt-5 text-white/75 text-lg max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      <div className="absolute -bottom-1 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}